// src/components/orders/ShareOrderModal.js
// ============================================================
// PARTAGER UNE COMMANDE — WhatsApp / Email / Impression
// ------------------------------------------------------------
// Cas d'usage : un client se presente au bureau, l'admin cree sa
// commande manuellement puis la lui transmet, sans quitter le
// back-office et sans lui demander de creer un compte.
//
// Le lien du recu PDF porte un token : il s'ouvre sans connexion.
// C'est ce lien qu'on envoie par WhatsApp ou par email.
//
// Le numero de telephone passe par waNumber() : sans normalisation,
// un numero saisi "0663..." ou "00212663..." produit un lien wa.me
// invalide (voir src/utils/whatsapp.js).
// Maxula
// ============================================================

import React, { useEffect, useState } from 'react';
import { FiX, FiMail, FiPrinter, FiCopy, FiSend, FiFileText, FiLoader, FiTag, FiCreditCard, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { invoiceAPI } from '../../services/api';
import { waNumber, waLink } from '../../utils/whatsapp';
import PayExpressCodeModal from './PayExpressCodeModal';

export default function ShareOrderModal({ orderNumber, onClose }) {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  // Ouverture de l'ecran du code PayExpress depuis une commande existante.
  const [showCode, setShowCode] = useState(false);

  // v1.398.0 — reemission des coupons (admin) quand la prestation a change
  const { user: authUser } = useAuth() || {};
  const isAdminUser = authUser?.role === 'admin';
  const [reissuing, setReissuing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reissue = async () => {
    if (!window.confirm("Réémettre les coupons de cette commande ?\n\nDe nouveaux coupons seront créés avec la prestation ACTUELLE de la commande. Les anciens codes ne fonctionneront plus : il faudra envoyer les nouveaux au client.")) return;
    setReissuing(true);
    try {
      const res = await invoiceAPI.reissueCoupons(orderNumber);
      toast.success(res?.data?.message || 'Coupons réémis');
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Réémission impossible');
    } finally {
      setReissuing(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await invoiceAPI.orderInvoice(orderNumber);
        const d = res?.data?.data || {};
        if (!alive) return;
        setInfo(d);
        setPhone(d?.customer?.phone || '');
        setEmail(d?.customer?.email || '');
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.message || 'Commande introuvable');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderNumber, reloadKey]);

  const total = Number(info?.total || info?.invoice?.totalTTC || 0);
  const pdfUrl = info?.pdfUrl || '';

  // Le PDF s'intitule BON DE COMMANDE tant que la commande n'est pas reglee, et
  // REÇU une fois payee (backend v1.411.0). Les libelles de l'ecran suivent la
  // meme regle : parler de « reçu » a un client qui n'a pas encore paye laisse
  // croire que c'est acquitte.
  const docLabel = info?.paid ? 'reçu' : 'bon de commande';
  const docLabelCap = info?.paid ? 'Reçu' : 'Bon de commande';

  // 'coupon' par defaut : au guichet on envoie le coupon, pas le montant.
  const [waMode, setWaMode] = useState('coupon');

  const coupons = Array.isArray(info?.coupons) ? info.coupons : [];

  // DEUX messages differents, et la distinction est le coeur du sujet :
  //   - COUPON  : ce qu'on envoie au client, et souvent a la personne a qui le
  //               coupon est offert. AUCUN MONTANT — un cadeau dont on lit le
  //               prix n'est plus un cadeau.
  //   - COMPTABLE : le recu ou le bon de commande, avec le total. Reserve aux
  //               cas ou le client demande un justificatif.
  //
  // Le message par defaut est celui du COUPON des qu'il en existe un : c'est le
  // cas courant au guichet. Avant, le seul message possible annoncait le total.
  const couponMessage = [
    `Bonjour${info?.customer?.name ? ' ' + info.customer.name : ''},`,
    ``,
    coupons.length > 1
      ? `Voici vos ${coupons.length} coupons Maxula :`
      : `Voici votre coupon Maxula :`,
    ``,
    ...coupons.map((c) => `${c.title ? c.title + '\n' : ''}${c.printUrl}`),
    ``,
    note || null,
    note ? `` : null,
    `Ouvrez le lien pour l'enregistrer en PDF ou l'imprimer.`,
    `À présenter au partenaire.`
  ].filter((l) => l !== null).join('\n');

  const accountingMessage = [
    `Bonjour${info?.customer?.name ? ' ' + info.customer.name : ''},`,
    ``,
    `Voici votre commande Maxula ${orderNumber}.`,
    `Total : ${total.toFixed(2)} MAD${info?.paid ? ' (payée)' : ''}`,
    note ? `` : null,
    note || null,
    ``,
    pdfUrl ? `Votre ${docLabel} : ${pdfUrl}` : null
  ].filter((l) => l !== null).join('\n');

  const message = waMode === 'coupon' && coupons.length ? couponMessage : accountingMessage;

  const openWhatsApp = () => {
    const num = waNumber(phone);
    if (phone && !num) {
      toast.error('Numéro invalide — format attendu : 212XXXXXXXXX');
      return;
    }
    // Sans numéro, WhatsApp demandera le destinataire : on n'empêche pas l'envoi.
    window.open(waLink(num, message), '_blank', 'noopener');
  };

  const sendEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())) {
      toast.error('Adresse email invalide');
      return;
    }
    setSending(true);
    try {
      const res = await invoiceAPI.sendOrderByEmail(orderNumber, { email: email.trim(), note });
      toast.success(res?.data?.message || 'Email envoyé');
    } catch (e) {
      toast.error(e?.response?.data?.message || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const printReceipt = () => {
    if (!pdfUrl) return toast.error('Document indisponible');
    // Le PDF s'ouvre dans un onglet : l'impression se fait depuis la visionneuse.
    window.open(pdfUrl, '_blank', 'noopener');
  };

  const copyLink = () => {
    if (!pdfUrl) return;
    try { navigator.clipboard.writeText(pdfUrl); toast.success('Lien copié'); }
    catch (e) { toast.error('Copie impossible'); }
  };

  if (showCode) {
    return (
      <PayExpressCodeModal
        orderId={info?.orderId}
        orderNumber={orderNumber}
        amount={total}
        customer={info?.customer || {}}
        onClose={() => setShowCode(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold">Partager la commande</h2>
            <p className="text-sm text-gray-500 font-mono">{orderNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Fermer">
            <FiX size={22} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
            <FiLoader className="animate-spin" /> Chargement…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <div>
                <span className="text-sm text-gray-600">Total</span>
                {/* L'agent au guichet doit savoir quel papier il s'apprete a
                    imprimer avant de cliquer, pas apres. */}
                <div className="text-xs text-gray-400">Document : {docLabelCap}</div>
              </div>
              <span className="font-bold text-[#e11d7b]">
                {total.toFixed(2)} MAD {info?.paid ? '· payée' : '· non payée'}
              </span>
            </div>

            {/* PAIEMENT EN AGENCE — retrouver le code d'une commande deja creee.
                C'est le SEUL endroit de l'interface ou l'on remet la main sur
                un code PayExpress : a la creation, il ne s'affiche qu'une fois,
                et fermer la fenetre le rendait introuvable alors qu'il est bien
                en base. Reserve aux commandes NON PAYEES : une commande reglee
                n'a plus de code a presenter. */}
            {!info?.paid ? (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FiCreditCard className="text-[#e11d7b]" size={18} />
                  <span className="font-semibold text-sm">Paiement en agence (PayExpress)</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {info?.payexpressCode
                    ? "Un code a déjà été émis pour cette commande. Ouvrez-le pour le relire ou le renvoyer au client."
                    : "Le client règlera en agence : générez son code de paiement et envoyez-le lui."}
                </p>
                {info?.payexpressCode ? (
                  <div className="mb-3 rounded-lg bg-[#fff5fa] border border-[#e11d7b]/30 px-3 py-2 text-center">
                    <div className="font-mono text-lg font-extrabold tracking-widest text-[#e11d7b] break-all">
                      {info.payexpressCode}
                    </div>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => setShowCode(true)}
                  className="w-full px-4 py-2 rounded-lg bg-[#e11d7b] text-white text-sm font-semibold hover:opacity-90 flex items-center justify-center gap-2"
                >
                  <FiCreditCard size={15} />
                  {info?.payexpressCode ? 'Ouvrir le code PayExpress' : 'Générer le code PayExpress'}
                </button>
              </div>
            ) : null}

            {/* COUPONS — un par unite achetee.
                Distincts du recu : le coupon ne porte AUCUN prix, parce qu'il
                s'offre. Le montant reste sur le recu et sur la facture, qui
                sont des documents separes. */}
            {Array.isArray(info?.coupons) && info.coupons.length === 0 ? (
              // Sans ce message, l'ecran ne montre QUE le bouton du bon de
              // commande et on croit que le coupon n'existe pas. Les coupons ne
              // sont crees qu'au paiement : c'est la contrepartie d'un document
              // qui vaut engagement, il ne peut pas exister avant.
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {info?.paid ? (
                  <>
                    <strong>Aucun coupon valable.</strong> La commande est payée mais ses
                    coupons ont été annulés (commande modifiée).
                    {isAdminUser && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={reissue}
                          disabled={reissuing}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700 disabled:opacity-60"
                        >
                          <FiRefreshCw size={12} className={reissuing ? 'animate-spin' : ''} />
                          {reissuing ? 'Émission…' : 'Émettre les coupons'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <strong>Pas encore de coupon.</strong> Les coupons sont générés au
                    moment où la commande est marquée payée. Encaissez puis rouvrez cet
                    écran : ils apparaîtront ici avec leur bouton d'impression.
                  </>
                )}
              </div>
            ) : null}

            {Array.isArray(info?.coupons) && info.coupons.length > 0 ? (
              <div className="border border-gray-200 rounded-lg">
                <div className="px-4 py-2 border-b bg-gray-50 text-xs font-semibold text-gray-600 flex items-center gap-2">
                  <FiTag size={13} /> Coupons ({info.coupons.length}) — sans prix, format A5
                  {isAdminUser && (
                    <button
                      type="button"
                      onClick={reissue}
                      disabled={reissuing}
                      className="ml-auto inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-60"
                      title="Recréer les coupons avec la prestation actuelle de la commande"
                    >
                      <FiRefreshCw size={11} className={reissuing ? 'animate-spin' : ''} />
                      {reissuing ? 'Réémission…' : 'Réémettre les coupons'}
                    </button>
                  )}
                </div>
                <div className="divide-y">
                  {info.coupons.map((c) => (
                    <div key={c.code} className="px-4 py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 truncate">
                          {c.title || 'Coupon'}
                        </div>
                        <div className="text-[11px] text-gray-500 font-mono">{c.code}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => window.open(c.printUrl, '_blank', 'noopener')}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
                      >
                        <FiPrinter size={13} /> Imprimer
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium mb-1">Message (optionnel)</label>
              <textarea
                rows="2"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ex : Merci de votre visite. Présentez ce reçu à votre arrivée."
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Ajouté au message WhatsApp et à l'email.</p>
            </div>

            {/* WhatsApp */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaWhatsapp className="text-green-600" size={18} />
                <span className="font-semibold text-sm">Envoyer par WhatsApp</span>
              </div>
              {/* Choix explicite de CE QU'ON ENVOIE. Deux boutons visibles plutot
                  qu'un reglage cache : l'agent doit voir d'un coup d'oeil qu'il
                  s'apprete a envoyer un montant, ou non. */}
              {coupons.length > 0 ? (
                <div className="flex gap-2 mb-2">
                  {[
                    ['coupon', `Coupon (sans prix)`],
                    ['accounting', `${docLabelCap} (avec prix)`],
                  ].map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setWaMode(v)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                        waMode === v
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="212XXXXXXXXX"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={openWhatsApp}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <FaWhatsapp size={15} /> Ouvrir WhatsApp
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Sans numéro, WhatsApp vous demandera le destinataire.
                {coupons.length > 0 && waMode === 'coupon'
                  ? " Le message contient le lien du coupon — aucun montant n'y figure. Le client l'ouvre et l'enregistre en PDF."
                  : ' Le message contient le total et le lien du document comptable.'}
              </p>
            </div>

            {/* Email */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FiMail className="text-blue-600" size={18} />
                <span className="font-semibold text-sm">Envoyer par email</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@exemple.com"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={sendEmail}
                  disabled={sending}
                  className="px-4 py-2 rounded-lg bg-[#e11d7b] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <FiSend size={15} /> {sending ? 'Envoi…' : 'Envoyer'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Le client reçoit le récapitulatif et un lien vers son {docLabel}, consultable sans compte.
              </p>
            </div>

            {/* Reçu */}
            <div className="flex flex-wrap gap-2 border-t pt-4">
              <button
                type="button"
                onClick={printReceipt}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
              >
                <FiPrinter size={15} /> Imprimer le {docLabel} (avec prix)
              </button>
              <button
                type="button"
                onClick={() => pdfUrl && window.open(pdfUrl, '_blank', 'noopener')}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
              >
                <FiFileText size={15} /> Voir le PDF
              </button>
              <button
                type="button"
                onClick={copyLink}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
              >
                <FiCopy size={15} /> Copier le lien
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
